'use client';

import { Button } from '@ahafood/ui/components/button';
import { Checkbox } from '@ahafood/ui/components/checkbox';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Textarea } from '@ahafood/ui/components/textarea';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import React, { useCallback, useMemo, useState } from 'react';

import { StepIndicator } from './step-indicator';
import { trackEvent } from '@/lib/event-tracking';
import { ALLERGIES, type Allergy, useAIPlannerStore } from '@/store/ai-planner';

export function StepAllergies() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const preferences = useAIPlannerStore.use.preferences();
  const setPreferences = useAIPlannerStore.use.setPreferences();
  const prevStep = useAIPlannerStore.use.prevStep();
  const setCurrentStep = useAIPlannerStore.use.setCurrentStep();

  const [userNote, setUserNote] = useState(preferences.userNote ?? '');
  const [noAllergies, setNoAllergies] = useState(false);

  const selectedAllergies = useMemo(() => {
    return preferences.allergies ?? [];
  }, [preferences.allergies]);

  const handleAllergyToggle = useCallback(
    (allergy: Allergy) => {
      if (noAllergies) {
        setNoAllergies(false);
      }
      const newAllergies = selectedAllergies.includes(allergy)
        ? selectedAllergies.filter((a) => a !== allergy)
        : [...selectedAllergies, allergy];
      setPreferences({ allergies: newAllergies });
    },
    [selectedAllergies, setPreferences, noAllergies]
  );

  const handleNoAllergiesToggle = useCallback(() => {
    setNoAllergies(!noAllergies);
    if (!noAllergies) {
      setPreferences({ allergies: [] });
    }
  }, [noAllergies, setPreferences]);

  const handleSubmit = useCallback(() => {
    // Track step completion
    trackEvent('meal_plan', {
      event_name: 'click_button',
      button_name: 'meal_plan_step',
      button_location: currentStep,
    });

    // Save user note if any
    if (userNote.trim()) {
      setPreferences({ userNote: userNote.trim() });
    }
    // Go to processing step
    setCurrentStep('processing');
  }, [userNote, setPreferences, setCurrentStep, currentStep]);

  return (
    <div className="p-0">
      {/* Background Image */}
      <div className="absolute -top-40 inset-x-0 w-full h-auto flex items-start justify-center py-4 aspect-square">
        <Image
          src={`${assetPrefix}/static/icons/ai-planner-page-2-bg.svg`}
          alt="planner allergies background"
          fill
          className="object-contain"
          priority
        />
      </div>

      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-88px-24px)] overflow-auto">
        {/* Title */}
        <div className="mb-6 mt-4">
          <h2 className="text-2xl font-bold text-[#3B82F6] leading-tight">
            Bạn có dị ứng
          </h2>
          <h2 className="text-2xl font-bold leading-tight">
            <span className="text-white bg-primary-40 rounded-full px-3 w-fit inline-block">
              hay lưu ý gì đặc biệt?
            </span>
          </h2>
        </div>

        {/* Allergies Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {ALLERGIES.slice(0, 8).map((allergy) => (
            <button
              key={allergy.value}
              onClick={() => handleAllergyToggle(allergy.value)}
              disabled={noAllergies}
              className={cn(
                'flex flex-col items-center gap-2 p-3 rounded-2xl transition-all backdrop-blur-sm',
                selectedAllergies.includes(allergy.value)
                  ? ' border-2 border-primary-40 ai-planner-button-active'
                  : ' border-2 border-transparent ai-planner-button',
                noAllergies && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="relative size-10">
                <Image
                  src={`${assetPrefix}/static/icons/${allergy.icon}`}
                  alt={allergy.label}
                  fill
                  className="object-contain"
                />
              </div>
              <span className="text-xs font-bold text-center text-white">
                {allergy.label}
              </span>
            </button>
          ))}
        </div>

        {/* No Allergies Checkbox */}
        <div className="flex items-center gap-3 py-3 mb-4">
          <Checkbox
            id="no-allergies"
            checked={noAllergies}
            onCheckedChange={() => handleNoAllergiesToggle()}
            className="border-white/30 data-[state=checked]:bg-[#ff8200] data-[state=checked]:border-[#ff8200] ai-planner-button"
          />
          <label
            htmlFor="no-allergies"
            className="text-white text-sm cursor-pointer font-medium"
          >
            Tôi không dị ứng
          </label>
        </div>

        {/* User Note */}
        <div className="mb-4">
          <label className="text-white text-sm font-semibold mb-2 block">
            Dị ứng hay lưu ý khác cho mọi đơn hàng:
          </label>
          <Textarea
            value={userNote}
            onChange={(e) => setUserNote(e.target.value)}
            placeholder="Không hành, không ớt,..."
            className="ai-planner-button resize-none bg-[#99999906] text-white placeholder:text-white/80 min-h-[100px]"
            rows={3}
          />
        </div>

        {/* Spacer for bottom button */}
        <div className="h-24" />
      </ScrollArea>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className="glass-bubble rounded-full flex justify-between items-center px-4 py-2 h-16">
          <Button
            variant="ghost"
            onClick={() => {
              trackEvent('meal_plan', {
                button_name: 'meal_plan_back',
                button_location: currentStep,
              });
              prevStep();
            }}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            ← Quay lại
          </Button>
          <StepIndicator />
          <Button
            variant="ghost"
            onClick={handleSubmit}
            className="text-white hover:bg-white/10 flex items-center gap-2 text-sm font-semibold"
          >
            Tiếp tục →
          </Button>
        </div>
      </div>
    </div>
  );
}
